import axios from 'axios'
import { apiConstants } from '../../common/constants'

// Reuses the same per-account-code raw-pincode endpoint the Bulk Serviceability
// "Account Codes" tab already calls (useBulkServiceability.js's downloadAccountCodeCsv) —
// GET /{accountCode}/download?pincodeType=. That endpoint returns one pincode per line
// (real Mongo data, not a count), so merging it across the codes behind a row is enough
// to answer "give me every pincode this row covers", however many codes make up the row.
const ROOT = apiConstants.BASE_LOCAL + apiConstants.BULK_SERV_BASE

async function fetchPincodes(accountCode, pincodeType) {
  try {
    const res = await axios.get(
      `${ROOT}/${encodeURIComponent(accountCode)}/download`,
      { params: { pincodeType }, responseType: 'text' }
    )
    const text = typeof res.data === 'string' ? res.data : ''
    return text
      .split('\n')
      .slice(1) // drop the "pincode" header line
      .map((s) => s.trim())
      .filter(Boolean)
  } catch (err) {
    return []
  }
}

const csvEsc = (v) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
}

/**
 * Fetch + merge + de-duplicate raw pincodes for one or more account codes and trigger a
 * client-side CSV download. `pincodeTypes` maps to what the page is showing (e.g. Delivery
 * pages merge DROP_PREPAID + DROP_COD; Pickup pages use PICKUP_PREPAID only) — never assume
 * a single type. `filterPincode`, when set (the page's own pincode-search box), narrows the
 * merged result down to that one pincode instead of exporting everything.
 *
 * Returns false when nothing came back (all codes failed, or the filtered pincode wasn't
 * found) so the caller can show its own error — this file has no UI/snackbar dependency.
 */
export async function downloadPincodesForRow({ accountCodes, pincodeTypes, filterPincode, filename }) {
  const codes = [...new Set((accountCodes || []).filter(Boolean))]
  const types = Array.isArray(pincodeTypes) ? pincodeTypes : [pincodeTypes]
  if (!codes.length || !types.length) return false

  const lists = await Promise.all(
    codes.flatMap((code) => types.map((t) => fetchPincodes(code, t)))
  )
  let merged = [...new Set(lists.flat())]
  if (filterPincode) merged = merged.filter((p) => p === filterPincode)
  if (!merged.length) return false
  merged.sort()

  const body = ['pincode', ...merged.map(csvEsc)].join('\n')
  const blob = new Blob([body], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || 'pincodes.csv'
  a.click()
  URL.revokeObjectURL(url)
  return true
}
